/**
 * TCX-917 — single source of truth for the promotional pop-up's shape, its
 * brand tokens and every validation rule. Consumed by BOTH sides:
 *   - BE: promo-popup.service.ts (enforcement; the DTO alone is not enough)
 *   - FE: the admin editor (inline errors) and the runtime pop-up
 * The website bundles this file directly through the `@promo-popup-contract`
 * alias, exactly as TCX-823 does with legal-html.contract.ts.
 *
 * This module MUST stay dependency-free (zero imports, no DOM/Node types): it
 * is type-checked by both the NestJS tsconfig and the website's strict React
 * tsconfig, and a single `@nestjs/common` import would break the FE build.
 *
 * A second copy of any list below — in a DTO, a component, a stylesheet — is
 * the AGENTS.md P0 "same rule implemented twice". Import from here instead.
 */

export const POPUP_AUDIENCES = ['consultant', 'franchisor'] as const;
export type PopupAudience = (typeof POPUP_AUDIENCES)[number];

export type PopupStatus = 'draft' | 'published' | 'off';

/**
 * Derived from the AntD theme (`colorPrimary: #2A4BC2`, the `#1c8271` accent
 * and `Typography.colorText` #192126) plus the TCX-917 prototype's swatches.
 * `#99B9FF` is the background both pop-ups already ship with, so a seeded
 * record reproduces today's look exactly.
 *
 * `#DC2626` (red) and `#FFA500` (amber) were added later, one increment after
 * the rest. Neither is an invention: `#FFA500` is the project's own `pending`
 * status colour (`statusColors` in pages/admin/bookings/index.tsx, and the
 * accepted/pending pair in LegalAcceptanceHistoryModal.tsx), and `#DC2626` is
 * the red it already uses for DESTRUCTIVE ACTIONS — the Reject-reschedule,
 * Late-cancel and Keep-fee buttons all set it as their backgroundColor.
 *
 * `#DC2626` rather than `#F68B8B`, which is the red of the same status map,
 * because this palette feeds BUTTON FILL and LABEL colour, not a status dot.
 * `#F68B8B` is tuned to be a 6px marker beside text; as a button fill under
 * white label text its contrast is poor. The colour a palette needs depends
 * on what the palette paints.
 *
 * Placement: each array already ends in its neutrals (white, or white then
 * dark) with the coloured swatches (blue, green, ...) running before them.
 * Red and amber are inserted as a pair right after the last coloured swatch
 * and before that neutral tail, so the neutrals stay the fixed endpoint of
 * every list. TEXT_COLORS is the one place this shifts an existing index —
 * white moves from position 4 to the new last position — so any caller that
 * needs "the default label colour" reads it as `TEXT_COLORS[length - 1]`,
 * never a literal `[4]` (see CtaCard.tsx, AlternateCtaList.tsx, PopupEditor.tsx).
 *
 * If design ever publishes an official palette, these five arrays are the only
 * place to change.
 */
export const BACKGROUND_COLORS = [
  '#99B9FF',
  '#F0F2FF',
  '#E8F5F0',
  '#DC2626',
  '#FFA500',
  '#FFFFFF',
  '#192126',
] as const;

export const TEXT_COLORS = [
  '#192126',
  '#5A6672',
  '#2A4BC2',
  '#1C8271',
  '#DC2626',
  '#FFA500',
  '#FFFFFF',
] as const;

export const BUTTON_FILLS = [
  '#2A4BC2',
  '#1C8271',
  '#DC2626',
  '#FFA500',
  '#192126',
  '#FFFFFF',
] as const;

/**
 * TCX-917 increment — the CTA button's visual treatment. 'solid' is today's
 * only look (coloured background, rounded); 'outline' and 'link' are new.
 * Kept as a closed list, same reasoning as every other palette here: the
 * renderer switches on these exact three strings, so a fourth value would
 * silently fall through to whichever branch happens to be the `else`.
 */
export const CTA_VARIANTS = ['solid', 'outline', 'link'] as const;
export type CtaVariant = (typeof CTA_VARIANTS)[number];

/**
 * One family, on purpose. `Lato` is the only font-family declared anywhere in
 * the website (styles/button.scss) and no webfont is loaded, so offering a
 * second option would mean inventing a brand font. The editor renders this as
 * a disabled select with helper text rather than hiding the control, because
 * AC 8 names font family explicitly.
 */
export const FONT_FAMILIES = ['Lato'] as const;
export const FONT_WEIGHTS = ['regular', 'bold', 'black'] as const;
export const TEXT_ALIGNMENTS = ['left', 'center'] as const;

export type FontFamily = (typeof FONT_FAMILIES)[number];
export type FontWeight = (typeof FONT_WEIGHTS)[number];
export type TextAlignment = (typeof TEXT_ALIGNMENTS)[number];

/**
 * Per-element, not one shared range: the pop-up card is a fixed width, and a
 * 40px body paragraph overflows it while a 40px headline is the design intent.
 */
export const FONT_SIZE_RANGE = {
  headline: { min: 14, max: 40 },
  body: { min: 10, max: 20 },
  ctaLabel: { min: 10, max: 20 },
} as const;

export type TextElement = keyof typeof FONT_SIZE_RANGE;

/**
 * TCX-917 increment (card width) — a ceiling on how much copy a text element
 * may carry, per element.
 *
 * This is a LAYOUT rule, not a style preference. The pop-up is a two-column
 * card with a fixed-width illustration beside the copy, and the copy is what
 * decides the card's height. Past a few hundred characters the card grows to
 * several times the artwork's own proportion, and no arrangement of that
 * artwork looks right: filling the column crops its edges away, and fitting
 * inside the column leaves it marooned in a field of background. Bounding the
 * copy is what keeps the card inside the range its design works in.
 *
 * Only `body` is bounded today. The table is keyed by element so headline and
 * ctaLabel can be added without changing any of the code that reads it — they
 * are unbounded for now because nothing has shown them to be a problem, not
 * because they are exempt in principle.
 *
 * Checked in STRICT (publish) mode only, on purpose, and the one rule here
 * that is: a draft is allowed to be mid-thought — the same reason a draft may
 * have an empty headline — and an admin part-way through trimming a long
 * paragraph must still be able to Save. Publish is where the card has to be
 * presentable.
 */
export const TEXT_MAX_LENGTHS: Partial<Record<TextElement, number>> = {
  body: 600,
};

/**
 * TCX-917 increment (auto-dismiss timer) — bounds on `PopupContent.
 * autoDismissSeconds`, the admin-set duration the pop-up stays on screen
 * before it closes itself.
 *
 * 5 seconds is the floor: anything shorter risks the pop-up closing before a
 * viewer has even finished reading the headline, which defeats the point of
 * showing it at all. 14 seconds is the ceiling: past that, an "auto-dismiss
 * timer" stops behaving like a timer and starts behaving like "never" — the
 * ticket asks for a bounded countdown with a visible progress line, not a
 * near-permanent pop-up that happens to carry one. Both bounds are integers —
 * the admin picks whole seconds, not fractions.
 */
export const AUTO_DISMISS_SECONDS_RANGE = { min: 5, max: 14 } as const;

/**
 * TCX-917 increment (image size) — bounds on `PopupContent.imageWidth`, the
 * rendered width of the artwork in CSS pixels.
 *
 * Width ONLY: the rendered height is derived from the uploaded file's own
 * dimensions, so scaling this never distorts the artwork and never crops it.
 * Storing a height too would be a second source of truth that could disagree
 * with the file, which is how the artwork ends up stretched.
 *
 * `default` is 218 — the width the artwork rendered at before this increment,
 * so a record with no `imageWidth` looks exactly as it did. 120 is the floor
 * (below that the illustration is a thumbnail) and 420 the ceiling (past that
 * it crowds out the copy even on the widest card). The renderer caps the
 * artwork at half the row besides, so a large value on a narrow card shrinks
 * proportionally rather than bursting it.
 *
 * `step` is an editor affordance ONLY, not validated — same as
 * POPUP_CARD_WIDTH_RANGE.
 */
export const POPUP_IMAGE_WIDTH_RANGE = {
  min: 120,
  max: 420,
  default: 218,
  step: 10,
} as const;

/**
 * TCX-917 increment (card width) — bounds on `PopupContent.cardWidth`, the
 * rendered max-width of the pop-up card in CSS pixels.
 *
 * `default` is what a record with no `cardWidth` renders at, in the live
 * pop-up and in the admin preview alike. It is the ONE place that number is
 * written down: PromoPopup.scss mirrors it as a CSS fallback and a guard test
 * pins the two together.
 *
 * 480 is the floor: the artwork panel is a fixed 218px, so below this the text
 * column drops under ~230px and the copy stops being readable. 1000 is the
 * ceiling: this is a corner overlay, not a modal, and past that it covers most
 * of a laptop viewport.
 *
 * `step` is an editor affordance ONLY — the stepper moves the value by this
 * much per click. It is deliberately NOT validated: a record sitting between
 * two steps (hand-edited, or saved before this step changed) is perfectly
 * valid, and rejecting it would block Save on an unrelated edit.
 */
export const POPUP_CARD_WIDTH_RANGE = {
  min: 480,
  max: 1000,
  default: 600,
  step: 10,
} as const;

/**
 * Deliberately the constraints that already exist in production, plus ONE
 * addition. Formats and the 15 MB cap mirror S3UploadService/FileInterceptor.
 * The 436x520 minimum is 2x the rendered 218x260, for retina.
 *
 * Aspect ratio is a WARNING, never a rejection: the pop-up has always cropped
 * with `object-fit: cover`, and every image in production today (1040x976,
 * 436x520, 1207x1303) is off 4:5. A blocking rule would refuse the artwork the
 * product is currently serving.
 */
export const IMAGE_RULES = {
  formats: ['png', 'jpeg', 'gif', 'webp'] as const,
  maxBytes: 15 * 1024 * 1024,
  minWidth: 436,
  minHeight: 520,
  preferredAspect: 4 / 5,
  aspectTolerance: 0.05,
} as const;

export type PopupImageFormat = (typeof IMAGE_RULES.formats)[number];

export const MAX_ALTERNATE_CTAS = 5;

/**
 * Closed list, per audience. Each id must be evaluable from data the frontend
 * already holds on the Redux profile — adding one means adding a branch in
 * promoPopupConditions.ts, so the two lists are checked against each other by
 * a test there.
 */
export const POPUP_CONDITIONS_BY_AUDIENCE = {
  consultant: ['subscription-inactive'],
  franchisor: [
    'not-full-member',
    'on-connect-tier',
    'is-free-listing',
    'no-premium-dashboard',
  ],
} as const;

export type PopupConditionId =
  | (typeof POPUP_CONDITIONS_BY_AUDIENCE.consultant)[number]
  | (typeof POPUP_CONDITIONS_BY_AUDIENCE.franchisor)[number];

export const POPUP_CONDITION_LABEL: Record<PopupConditionId, string> = {
  'subscription-inactive': 'Subscription is not active',
  'not-full-member': 'Is not a full TCX member',
  'on-connect-tier': 'Is on the Connect tier',
  'is-free-listing': 'Is a Free Listing',
  'no-premium-dashboard': 'Has no premium dashboard page',
};

export interface PopupImage {
  url: string;
  width: number;
  height: number;
  bytes: number;
  format: PopupImageFormat;
  originalName: string;
}

export interface TextBlock {
  text: string;
  fontFamily: FontFamily;
  fontSize: number;
  fontWeight: FontWeight;
  color: string;
  align: TextAlignment;
}

/**
 * TCX-917 increment (preset redirect actions) — closed, per-audience list of
 * CTA destinations resolved at RENDER time from data already on the viewer's
 * own Redux profile, rather than a URL the admin types in. Mirrors
 * `POPUP_CONDITIONS_BY_AUDIENCE` above for the same reason: `premium-dashboard`
 * only makes sense for a franchisor (that field does not exist on a
 * consultant's profile at all), so offering it to the consultant audience
 * would let an admin build a button that can never resolve for anyone who
 * sees it.
 *
 * Resolution itself — reading the viewer's profile, deciding whether a stored
 * value is safe to use — happens on the FRONTEND, in
 * `promoPopupConditions.ts`, alongside the condition predicates that already
 * read the same profile. This contract only defines and validates the closed
 * set of ids; it never sees a Redux store or a route table.
 */
export const CTA_ACTION_PRESETS_BY_AUDIENCE = {
  consultant: ['edit-profile', 'subscription'],
  franchisor: ['edit-profile', 'subscription', 'premium-dashboard'],
} as const;

export type CtaActionPreset =
  | (typeof CTA_ACTION_PRESETS_BY_AUDIENCE.consultant)[number]
  | (typeof CTA_ACTION_PRESETS_BY_AUDIENCE.franchisor)[number];

/** Editor copy for the Action select's preset options (CtaFields). */
export const CTA_ACTION_PRESET_LABEL: Record<CtaActionPreset, string> = {
  'premium-dashboard': 'Redirect to premium dashboard',
  'edit-profile': 'Redirect to edit profile',
  subscription: 'Redirect to subscription page',
};

/**
 * TCX-917 increment (preset redirect actions) — `url` and `support` are
 * unchanged from before this increment; a record saved with either still
 * validates and renders exactly as it did. The three preset ids are new
 * alternatives to `url`, closed and validated the same way every other list
 * in this file is: `action.type` must be one of these five strings, and
 * which of the three presets is offered depends on the record's audience
 * (see `CTA_ACTION_PRESETS_BY_AUDIENCE`).
 */
export type CtaAction =
  | { type: 'url'; url: string }
  | { type: 'support' }
  | { type: CtaActionPreset };

export interface CtaButton {
  label: string;
  action: CtaAction;
  fontFamily: FontFamily;
  fontSize: number;
  fontWeight: FontWeight;
  align: TextAlignment;
  labelColor: string;
  fill: string;
  /**
   * TCX-917 increment — every field below is OPTIONAL, and stays optional in
   * BOTH `validatePopupContent` and `validatePopupDraft` (unlike `fill`/
   * `labelColor`, which strict/publish mode requires). A record saved before
   * this increment has none of them at all; making any one required would
   * reject that record outright. `variant` defaults to 'solid' — and every
   * colour falls back to its non-hover counterpart — at RENDER time
   * (PopupCard), not here, so an absent value renders exactly as it does
   * today.
   */
  variant?: CtaVariant;
  /** Meaningless for 'link' (never has a border) — the editor hides this
   * control for that variant instead of showing a value that does nothing. */
  borderColor?: string;
  /**
   * Hover overrides. The editor always shows these three (there is no
   * variant where a hover recolour is unreachable the way the base fill/
   * border controls are), but PopupCard only APPLIES `hoverFill` and
   * `hoverBorderColor` for 'solid'/'outline' — 'link' keeps its "no
   * background, no border" look on hover too, per the render contract, so a
   * value stored here for a link CTA is saved but has no visible effect.
   */
  hoverFill?: string;
  hoverLabelColor?: string;
  hoverBorderColor?: string;
  /**
   * TCX-917 increment — same `isPresent`-checked, optional-forever shape as
   * `variant`/`borderColor`/the hover colours above: a record saved before
   * this increment has no `fullWidth` at all, and PopupCard treats an absent
   * value as `false` — today's shrink-to-content button — so nothing already
   * saved changes shape.
   *
   * `true` makes the button span the full width of its column (PopupCard
   * passes AntD's own `block` prop rather than a hand-rolled width rule).
   * Once the button fills that width there is no room left for `align` to
   * move it, so the editor hides the alignment control whenever this is
   * `true` — see CtaFields.
   */
  fullWidth?: boolean;
}

/**
 * TCX-917 increment (rule style inheritance) — the six fields a rule button
 * never owns. A rule "REPLACES" the button it belongs to (the primary `cta`
 * or `secondaryCta.button`) only for a matched viewer, and the user's
 * decision was explicit: a rule may change only its label, its action (and
 * link), and its six colours. Layout — variant, full-width, alignment and
 * every font property — always comes from the OWNER button, never from the
 * rule, so a rule can never render as a visually different kind of button
 * than the one it stands in for.
 */
export type CtaInheritedLayoutField =
  | 'variant'
  | 'fullWidth'
  | 'align'
  | 'fontFamily'
  | 'fontSize'
  | 'fontWeight';

/**
 * A rule button. Structurally `CtaButton` minus the six inherited fields,
 * with those same six added back as OPTIONAL — not required, and not
 * validated for presence even on strict/publish validation (see
 * `validateCtaButton`'s `isRule` parameter). Optional rather than absent
 * entirely so a record saved before this increment (when a rule carried the
 * primary's full field set, including its own `variant`/`fullWidth`/etc.)
 * still satisfies the type and still validates — `applyOwnerLayout` (below)
 * overrides whatever is stored there at render time regardless, so a stale
 * value can never surface as a mismatch, but it is also never a type error to
 * still have one sitting on an old record.
 *
 * New rules never populate them at all (see AlternateCtaList's `newAlternate`
 * and `stripOwnerLayout` below) — storing a value nothing ever reads is the
 * trap this increment avoids for every rule saved from now on.
 */
export type AlternateCta = Omit<CtaButton, CtaInheritedLayoutField> &
  Partial<Pick<CtaButton, CtaInheritedLayoutField>> & {
    condition: PopupConditionId;
  };

/**
 * TCX-917 increment — the ONE place a rule's button is combined with the
 * layout of the button it replaces. `owner` always wins for the six
 * inherited fields, even when `rule` still carries a stored value for one of
 * them (a record saved before this increment) — inheriting is not
 * conditional on the rule's own field being absent, so a stale stored value
 * can never render differently than a freshly-created rule would.
 *
 * Both callers that turn a matched `AlternateCta` into something paintable
 * route through this: the live resolver (`promoPopupConditions.ts`'s
 * `resolveButton`) and the admin's manual preview switcher
 * (`PopupPreviewCard`) and its live edit view (`AlternateCtaList`, so the
 * editor's own colour-dependent controls — e.g. which fill/border pickers
 * apply — read the OWNER's variant, not a rule value that no longer means
 * anything). One function, so those three can never resolve inheritance three
 * slightly different ways.
 */
export const applyOwnerLayout = (
  owner: CtaButton,
  rule: AlternateCta,
): CtaButton => ({
  ...rule,
  variant: owner.variant,
  fullWidth: owner.fullWidth,
  align: owner.align,
  fontFamily: owner.fontFamily,
  fontSize: owner.fontSize,
  fontWeight: owner.fontWeight,
});

/**
 * The inverse, used only by the editor when a rule is saved: strips the six
 * inherited fields back off a fully-merged display value (see
 * `applyOwnerLayout`) before it is written into `alternateCtas`, so editing a
 * rule's label or colours can never leave an owner-layout snapshot sitting on
 * the record — the exact thing `AlternateCta`'s own doc comment calls a trap.
 */
export const stripOwnerLayout = (
  button: CtaButton,
  condition: PopupConditionId,
): AlternateCta => ({
  label: button.label,
  action: button.action,
  labelColor: button.labelColor,
  fill: button.fill,
  borderColor: button.borderColor,
  hoverFill: button.hoverFill,
  hoverLabelColor: button.hoverLabelColor,
  hoverBorderColor: button.hoverBorderColor,
  condition,
});

export const SECONDARY_CTA_POSITIONS = ['beside', 'below'] as const;
export type SecondaryCtaPosition = (typeof SECONDARY_CTA_POSITIONS)[number];

/**
 * TCX-917 increment — a second, independent button. It carries its OWN
 * button and its OWN up-to-`MAX_ALTERNATE_CTAS` rules, evaluated completely
 * separately from the primary's `cta`/`alternateCtas` — the two budgets are
 * not shared, and a viewer can match a secondary rule without matching (or
 * even having) a primary one.
 *
 * `position` is the admin's choice of where it renders relative to the
 * primary — PopupCard reads it to toggle layout, it never infers one.
 */
export interface SecondaryCta {
  button: CtaButton;
  alternateCtas: AlternateCta[];
  position: SecondaryCtaPosition;
}

export interface PopupContent {
  image: PopupImage;
  backgroundColor: string;
  headline: TextBlock;
  body: TextBlock;
  cta: CtaButton;
  /**
   * ORDER IS PRIORITY. Evaluation walks this array top-down and the first
   * matching alternate wins. A numeric `priority` field would be a second
   * source of truth that drifts from the order the admin sees on screen.
   */
  alternateCtas: AlternateCta[];
  /**
   * TCX-917 increment — OPTIONAL, and stays optional forever, same reasoning
   * as `cta.variant`/`cta.borderColor`/the hover fields above: a record saved
   * before this increment has no `secondaryCta` at all, and its absence means
   * exactly one thing — no second button — not "not configured yet" under
   * strict/publish validation. Validated with `isPresent(...)`, not
   * `shouldCheck(...)`, for that reason: `shouldCheck` collapses to "always
   * check" in strict mode, which would make an absent `secondaryCta` a
   * publish blocker.
   */
  secondaryCta?: SecondaryCta;
  /**
   * TCX-917 increment (auto-dismiss timer) — OPTIONAL, and stays optional
   * forever, same reasoning as `secondaryCta` above: a record saved before
   * this increment has no `autoDismissSeconds` at all, and its absence means
   * exactly one thing — no timer, the pop-up stays on screen until the
   * viewer clicks the X — not "not configured yet" under strict/publish
   * validation. Validated with `isPresent(...)`, not `shouldCheck(...)`, for
   * the same reason `secondaryCta` is: `shouldCheck` collapses to "always
   * check" in strict mode, which would make an absent `autoDismissSeconds` a
   * publish blocker for every pre-increment record.
   *
   * Whole seconds, bounded by `AUTO_DISMISS_SECONDS_RANGE`. The runtime pop-up
   * (PromoPopup) reads this to start its own countdown; this contract never
   * runs a timer itself.
   */
  autoDismissSeconds?: number;
  /**
   * TCX-917 increment (card width) — OPTIONAL, and stays optional forever,
   * same reasoning as `autoDismissSeconds` above. Its absence does NOT mean
   * "not configured yet": it means the card renders at
   * `POPUP_CARD_WIDTH_RANGE.default`, which is what every record saved before
   * this increment gets. Validated with `isPresent(...)`, not
   * `shouldCheck(...)`, so an absent value can never become a publish blocker.
   *
   * Whole CSS pixels, bounded by `POPUP_CARD_WIDTH_RANGE`. Only the CARD's own
   * max-width: the artwork panel keeps its fixed 218px, so widening the card
   * widens the text column, not the image.
   */
  cardWidth?: number;
  /**
   * TCX-917 increment (image size) — OPTIONAL forever, same shape as
   * `cardWidth` above: absent means "render the artwork at
   * POPUP_IMAGE_WIDTH_RANGE.default", which is the width it rendered at before
   * this increment, not "not configured yet".
   *
   * The WIDTH only. The height is computed from `image.width`/`image.height`
   * at render time, so the artwork always keeps the proportions of the file
   * the admin uploaded.
   */
  imageWidth?: number;
}

const includes = (list: readonly string[], value: unknown): boolean =>
  typeof value === 'string' && list.includes(value);

const isBlank = (value: unknown): boolean =>
  typeof value !== 'string' || value.trim().length === 0;

/**
 * TCX-917 Task 6b — the single knob that separates the strict rule from the
 * draft rule. `requirePresence: true` is `validatePopupContent`'s behaviour
 * today, unchanged. `requirePresence: false` is `validatePopupDraft`: every
 * per-value check below still runs, but only when the admin has actually put
 * something in that field — an admin building a pop-up from an empty screen
 * has not "failed" a rule they have not reached yet.
 */
interface ValidateOptions {
  requirePresence: boolean;
}

/** A value the admin has actually set — as opposed to still-untouched. */
const isPresent = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

/**
 * Whether a per-value rule should run at all: always in strict mode, and in
 * draft mode only once the field is no longer blank/untouched — an unset
 * field cannot fail a rule about what it contains.
 */
const shouldCheck = (value: unknown, opts: ValidateOptions): boolean =>
  opts.requirePresence || isPresent(value);

/**
 * `http(s)` only. A `javascript:` value saved through the admin must be
 * rejected AT SAVE TIME, not silently refuse to navigate on click — the FE's
 * openExternal would drop it, leaving a button that looks live and does
 * nothing.
 */
/**
 * TCX-917 review (finding 1) — whether two stored images are the same upload.
 *
 * Used by `saveDraft` to decide whether an incoming image needs a receipt: an
 * image byte-identical to the one already on the record was verified when it
 * was first saved, so a Save after a page reload — which loses the receipt the
 * upload returned — must not be refused.
 *
 * Compares the fields individually rather than by serialising both sides: a
 * `PopupImage` that arrived over JSON and one read back from Mongo can differ
 * in key order and in extra keys Mongoose adds, and neither difference means
 * a different image.
 */
export const sameImage = (
  a: PopupImage | undefined,
  b: PopupImage | undefined,
): boolean => {
  if (!a || !b) return false;
  return (
    a.url === b.url &&
    a.width === b.width &&
    a.height === b.height &&
    a.bytes === b.bytes &&
    a.format === b.format &&
    a.originalName === b.originalName
  );
};

export const isHttpUrl = (value: unknown): boolean => {
  if (typeof value !== 'string' || value.trim().length === 0) return false;
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

const validateTypography = (
  // TCX-917 increment (rule style inheritance) — optional, not required: an
  // `AlternateCta` (a rule) has these four as optional fields, and this
  // shape must accept that union member without a cast. `unknown` already
  // tolerates an absent/undefined value at the call sites below.
  block: {
    fontFamily?: unknown;
    fontSize?: unknown;
    fontWeight?: unknown;
    align?: unknown;
  },
  element: TextElement,
  path: string,
  opts: ValidateOptions,
  // TCX-917 increment (rule style inheritance) — true for a rule's own
  // typography fields, which are never required even on strict/publish
  // validation: they are inherited from the owner button (see
  // `CtaInheritedLayoutField`/`applyOwnerLayout`), so an absent value on a
  // rule is not "not reached yet", it is "will never be the rule's own".
  // `shouldCheck` alone cannot express that — it always requires presence
  // once `opts.requirePresence` is true, which is exactly the primary/
  // secondary owner's behaviour this must NOT apply to a rule.
  layoutIsInherited = false,
): string[] => {
  const errors: string[] = [];
  const range = FONT_SIZE_RANGE[element];
  const check = (value: unknown): boolean =>
    layoutIsInherited ? isPresent(value) : shouldCheck(value, opts);
  if (check(block.fontFamily) && !includes(FONT_FAMILIES, block.fontFamily)) {
    errors.push(
      `${path}.fontFamily: must be one of ${FONT_FAMILIES.join(', ')}`,
    );
  }
  if (
    check(block.fontSize) &&
    (typeof block.fontSize !== 'number' ||
      !Number.isInteger(block.fontSize) ||
      block.fontSize < range.min ||
      block.fontSize > range.max)
  ) {
    errors.push(
      `${path}.fontSize: must be between ${range.min} and ${range.max}`,
    );
  }
  if (check(block.fontWeight) && !includes(FONT_WEIGHTS, block.fontWeight)) {
    errors.push(
      `${path}.fontWeight: must be one of ${FONT_WEIGHTS.join(', ')}`,
    );
  }
  if (check(block.align) && !includes(TEXT_ALIGNMENTS, block.align)) {
    errors.push(`${path}.align: must be one of ${TEXT_ALIGNMENTS.join(', ')}`);
  }
  return errors;
};

const validateTextBlock = (
  block: TextBlock,
  element: TextElement,
  path: string,
  opts: ValidateOptions,
): string[] => {
  const errors: string[] = [];
  // Presence itself is the ONE thing a draft drops — an untouched text field
  // is not a validation failure, it is a field the admin has not reached yet.
  if (opts.requirePresence && isBlank(block?.text)) {
    errors.push(`${path}.text: must not be empty`);
  }
  if (shouldCheck(block?.color, opts) && !includes(TEXT_COLORS, block?.color)) {
    errors.push(
      `${path}.color: ${String(block?.color)} is not a brand text colour`,
    );
  }
  // TCX-917 increment (card width) — strict mode only; see TEXT_MAX_LENGTHS.
  // Guarded on the element having a limit at all, so an unbounded element is
  // simply not checked rather than checked against `undefined`.
  const maxLength = TEXT_MAX_LENGTHS[element];
  if (
    opts.requirePresence &&
    typeof maxLength === 'number' &&
    typeof block?.text === 'string' &&
    block.text.length > maxLength
  ) {
    errors.push(
      `${path}.text: must be ${maxLength} characters or fewer (currently ${block.text.length})`,
    );
  }
  return [
    ...errors,
    ...validateTypography(block ?? ({} as TextBlock), element, path, opts),
  ];
};

const validateCtaButton = (
  // TCX-917 increment (rule style inheritance) — widened from `CtaButton` so
  // an `AlternateCta` (whose six layout fields are optional) can be passed
  // straight through without a cast: every access below already goes through
  // `?.`, so nothing here actually requires those fields to be present.
  button: CtaButton | AlternateCta | undefined,
  path: string,
  // TCX-917 increment (preset redirect actions) — which of the three preset
  // action ids are valid depends on the record's audience (only a
  // franchisor can ever have a `premiumDashboardUrl`). Threaded through from
  // `validateCtaWithAlternates`, which already carries it for the exact same
  // reason `condition` below is checked against it.
  audience: PopupAudience,
  opts: ValidateOptions,
  // TCX-917 increment (rule style inheritance) — true when `button` is a
  // rule (an `AlternateCta`), whose `fontFamily`/`fontSize`/`fontWeight`/
  // `align` are never its own — see `validateTypography`'s own parameter of
  // the same name. False (the default) is the primary/secondary owner's
  // unchanged behaviour: those four fields ARE required on strict/publish
  // validation.
  isRule = false,
): string[] => {
  const errors: string[] = [];
  if (opts.requirePresence && isBlank(button?.label)) {
    errors.push(`${path}.label: must not be empty`);
  }
  if (
    shouldCheck(button?.labelColor, opts) &&
    !includes(TEXT_COLORS, button?.labelColor)
  ) {
    errors.push(
      `${path}.labelColor: ${String(button?.labelColor)} is not a brand text colour`,
    );
  }
  if (
    shouldCheck(button?.fill, opts) &&
    !includes(BUTTON_FILLS, button?.fill)
  ) {
    errors.push(
      `${path}.fill: ${String(button?.fill)} is not a brand button fill`,
    );
  }
  // TCX-917 increment — deliberately `isPresent`, NOT `shouldCheck`: these
  // five fields must stay optional even under `opts.requirePresence` (strict/
  // publish), unlike `fill`/`labelColor` above. `shouldCheck` collapses to
  // "always check" in strict mode, which would make an absent value a publish
  // blocker — exactly the regression the compatibility requirement forbids,
  // since a pre-increment record (and a 'link'/'outline' CTA, whose editor
  // hides some of these controls) has no way to fill them in. The membership
  // check itself is the SAME `includes(...)` helper every other colour field
  // above uses — no second check written for this.
  if (isPresent(button?.variant) && !includes(CTA_VARIANTS, button?.variant)) {
    errors.push(`${path}.variant: must be one of ${CTA_VARIANTS.join(', ')}`);
  }
  if (
    isPresent(button?.borderColor) &&
    !includes(BUTTON_FILLS, button?.borderColor)
  ) {
    errors.push(
      `${path}.borderColor: ${String(button?.borderColor)} is not a brand button fill`,
    );
  }
  if (
    isPresent(button?.hoverFill) &&
    !includes(BUTTON_FILLS, button?.hoverFill)
  ) {
    errors.push(
      `${path}.hoverFill: ${String(button?.hoverFill)} is not a brand button fill`,
    );
  }
  if (
    isPresent(button?.hoverBorderColor) &&
    !includes(BUTTON_FILLS, button?.hoverBorderColor)
  ) {
    errors.push(
      `${path}.hoverBorderColor: ${String(button?.hoverBorderColor)} is not a brand button fill`,
    );
  }
  if (
    isPresent(button?.hoverLabelColor) &&
    !includes(TEXT_COLORS, button?.hoverLabelColor)
  ) {
    errors.push(
      `${path}.hoverLabelColor: ${String(button?.hoverLabelColor)} is not a brand text colour`,
    );
  }
  // Same `isPresent` reasoning as the five fields above: `fullWidth` must
  // stay optional under strict/publish validation too, since a pre-increment
  // record has no such field at all. There is no closed palette to check
  // against — a boolean is either a boolean or it is not one.
  if (isPresent(button?.fullWidth) && typeof button?.fullWidth !== 'boolean') {
    errors.push(`${path}.fullWidth: must be a boolean`);
  }
  const action = button?.action;
  // TCX-917 increment (preset redirect actions) — the closed set of presets
  // THIS record's audience may use. `url` and `support` stay valid for
  // every audience, unchanged from before this increment.
  const allowedPresets: readonly string[] =
    CTA_ACTION_PRESETS_BY_AUDIENCE[audience] ?? [];
  // An action the admin has not configured at all is exactly the "not reached
  // yet" case a draft allows; once it exists, its shape is still enforced —
  // and a non-empty url on it is checked even if the url itself is optional.
  if (opts.requirePresence || action !== undefined) {
    if (action?.type === 'url') {
      if (shouldCheck(action.url, opts) && !isHttpUrl(action.url)) {
        errors.push(`${path}.action.url: must be an http(s) URL`);
      }
    } else if (
      action?.type !== 'support' &&
      !includes(allowedPresets, action?.type)
    ) {
      errors.push(
        `${path}.action.type: must be 'url', 'support', or one of ${allowedPresets.join(', ')} for ${audience}`,
      );
    }
  }
  return [
    ...errors,
    ...validateTypography(
      button ?? ({} as CtaButton),
      'ctaLabel',
      path,
      opts,
      isRule,
    ),
  ];
};

/**
 * TCX-917 increment — a button plus its own up-to-`MAX_ALTERNATE_CTAS` rules,
 * validated as one unit. Both the primary (`cta`/`alternateCtas`) and the
 * secondary (`secondaryCta.button`/`secondaryCta.alternateCtas`) route
 * through THIS function rather than each carrying its own copy of "cap the
 * list, validate every button, check every condition against the audience" —
 * the two budgets stay independent (a full primary list says nothing about
 * the secondary's) precisely because each call gets its own `alternates`
 * array and its own path prefix.
 */
const validateCtaWithAlternates = (
  button: CtaButton | undefined,
  alternates: AlternateCta[] | undefined,
  buttonPath: string,
  alternatesPath: string,
  audience: PopupAudience,
  opts: ValidateOptions,
): string[] => {
  const errors: string[] = [
    ...validateCtaButton(button, buttonPath, audience, opts),
  ];

  // Structural, not per-value: the alternates count and each condition's fit
  // with the audience are checked regardless of mode — there is no "have not
  // reached it yet" state for a button the admin already added.
  const list = alternates ?? [];
  if (list.length > MAX_ALTERNATE_CTAS) {
    errors.push(
      `${alternatesPath}: at most ${MAX_ALTERNATE_CTAS} alternate buttons`,
    );
  }
  const allowed: readonly string[] =
    POPUP_CONDITIONS_BY_AUDIENCE[audience] ?? [];
  list.forEach((alternate, i) => {
    // `isRule: true` — a rule's variant/fullWidth/align/font* are inherited
    // from `button` (the owner), never its own, so they must never be
    // required here even though `button` itself (validated above) still
    // requires them on strict/publish validation.
    errors.push(
      ...validateCtaButton(
        alternate,
        `${alternatesPath}[${i}]`,
        audience,
        opts,
        true,
      ),
    );
    if (
      shouldCheck(alternate?.condition, opts) &&
      !includes(allowed, alternate?.condition)
    ) {
      errors.push(
        `${alternatesPath}[${i}].condition: ${String(alternate?.condition)} is not available for ${audience}`,
      );
    }
  });

  return errors;
};

/**
 * Shared core behind BOTH public validators (TCX-917 Task 6b). Strict mode
 * (`requirePresence: true`) is byte-for-byte what `validatePopupContent`
 * enforced before this flag existed. Draft mode drops only the presence
 * requirements; every per-value rule still applies to whatever IS set, so the
 * two functions cannot drift apart into two copies of the same rules.
 */
const validatePopupCore = (
  content: PopupContent,
  audience: PopupAudience,
  opts: ValidateOptions,
): string[] => {
  const errors: string[] = [];

  if (
    shouldCheck(content?.backgroundColor, opts) &&
    !includes(BACKGROUND_COLORS, content?.backgroundColor)
  ) {
    errors.push(
      `backgroundColor: ${String(content?.backgroundColor)} is not a brand background colour`,
    );
  }

  // TCX-917 increment (auto-dismiss timer) — `isPresent`, not `shouldCheck`,
  // same reasoning as `secondaryCta` below: this must stay optional forever,
  // in BOTH strict and draft mode, so an absent value (no timer) can never
  // become a publish blocker — see the field's own doc comment on
  // `PopupContent`.
  if (
    isPresent(content?.autoDismissSeconds) &&
    (typeof content.autoDismissSeconds !== 'number' ||
      !Number.isInteger(content.autoDismissSeconds) ||
      content.autoDismissSeconds < AUTO_DISMISS_SECONDS_RANGE.min ||
      content.autoDismissSeconds > AUTO_DISMISS_SECONDS_RANGE.max)
  ) {
    errors.push(
      `autoDismissSeconds: must be an integer between ${AUTO_DISMISS_SECONDS_RANGE.min} and ${AUTO_DISMISS_SECONDS_RANGE.max}`,
    );
  }

  // TCX-917 increment (card width) — `isPresent`, not `shouldCheck`, for the
  // same reason `autoDismissSeconds` above uses it: an absent `cardWidth`
  // means "render at the default width", not "unfilled", so it must never
  // block a publish. `step` is NOT checked here — see the range's own comment.
  if (
    isPresent(content?.cardWidth) &&
    (typeof content.cardWidth !== 'number' ||
      !Number.isInteger(content.cardWidth) ||
      content.cardWidth < POPUP_CARD_WIDTH_RANGE.min ||
      content.cardWidth > POPUP_CARD_WIDTH_RANGE.max)
  ) {
    errors.push(
      `cardWidth: must be an integer between ${POPUP_CARD_WIDTH_RANGE.min} and ${POPUP_CARD_WIDTH_RANGE.max}`,
    );
  }

  // TCX-917 increment (image size) — `isPresent`, not `shouldCheck`, for the
  // same reason `cardWidth` above uses it: absent means "render at the default
  // width", not "unfilled", so it must never block a publish.
  if (
    isPresent(content?.imageWidth) &&
    (typeof content.imageWidth !== 'number' ||
      !Number.isInteger(content.imageWidth) ||
      content.imageWidth < POPUP_IMAGE_WIDTH_RANGE.min ||
      content.imageWidth > POPUP_IMAGE_WIDTH_RANGE.max)
  ) {
    errors.push(
      `imageWidth: must be an integer between ${POPUP_IMAGE_WIDTH_RANGE.min} and ${POPUP_IMAGE_WIDTH_RANGE.max}`,
    );
  }

  const image = content?.image;
  if (shouldCheck(image?.url, opts) && !isHttpUrl(image?.url)) {
    errors.push('image.url: must be an http(s) URL');
  }
  if (
    shouldCheck(image?.format, opts) &&
    !includes(IMAGE_RULES.formats, image?.format)
  ) {
    errors.push(
      `image.format: must be one of ${IMAGE_RULES.formats.join(', ')}`,
    );
  }

  errors.push(
    ...validateTextBlock(content?.headline, 'headline', 'headline', opts),
  );
  errors.push(...validateTextBlock(content?.body, 'body', 'body', opts));
  errors.push(
    ...validateCtaWithAlternates(
      content?.cta,
      content?.alternateCtas,
      'cta',
      'alternateCtas',
      audience,
      opts,
    ),
  );

  // TCX-917 increment — `isPresent`, not `shouldCheck`: a `secondaryCta` must
  // stay optional forever (see the field's own doc comment on `PopupContent`),
  // in BOTH strict and draft mode, unlike the primary `cta` above which is
  // always validated. Once it exists, everything inside it — button, rules,
  // position — goes through the exact same rules the primary uses.
  const secondary = content?.secondaryCta;
  if (isPresent(secondary)) {
    errors.push(
      ...validateCtaWithAlternates(
        secondary?.button,
        secondary?.alternateCtas,
        'secondaryCta.button',
        'secondaryCta.alternateCtas',
        audience,
        opts,
      ),
    );
    if (
      shouldCheck(secondary?.position, opts) &&
      !includes(SECONDARY_CTA_POSITIONS, secondary?.position)
    ) {
      errors.push(
        `secondaryCta.position: must be one of ${SECONDARY_CTA_POSITIONS.join(', ')}`,
      );
    }
  }

  return errors;
};

/**
 * Returns EVERY problem, not the first. The editor shows inline errors per
 * field, so a validator that short-circuits would make the admin fix one
 * thing, save, and discover the next.
 *
 * The full rule: every field is required AND every value must be valid. Used
 * by `publish` (TCX-917 Task 6b) — nothing goes live unless it is complete.
 */
export const validatePopupContent = (
  content: PopupContent,
  audience: PopupAudience,
): string[] => validatePopupCore(content, audience, { requirePresence: true });

/**
 * TCX-917 Task 6b — the relaxed rule `saveDraft` uses. An admin building a
 * pop-up from an empty screen must be able to Save before headline, body, CTA
 * or image are filled in, or the first Save loses whatever they typed. Every
 * value that IS set is still checked by the exact same rules as
 * `validatePopupContent` — see `validatePopupCore` above, the single place
 * those rules live.
 */
export const validatePopupDraft = (
  content: PopupContent,
  audience: PopupAudience,
): string[] => validatePopupCore(content, audience, { requirePresence: false });

export const validateImageMeta = (meta: {
  width: number;
  height: number;
  bytes: number;
  format: PopupImageFormat;
}): { errors: string[]; warnings: string[] } => {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!includes(IMAGE_RULES.formats, meta.format)) {
    errors.push(`Image must be one of: ${IMAGE_RULES.formats.join(', ')}`);
  }
  if (
    meta.width < IMAGE_RULES.minWidth ||
    meta.height < IMAGE_RULES.minHeight
  ) {
    errors.push(
      `Image must be at least ${IMAGE_RULES.minWidth}x${IMAGE_RULES.minHeight} px`,
    );
  }
  if (meta.bytes > IMAGE_RULES.maxBytes) {
    errors.push(
      `Image must be ${IMAGE_RULES.maxBytes / (1024 * 1024)} MB or smaller`,
    );
  }
  if (
    meta.height > 0 &&
    Math.abs(meta.width / meta.height - IMAGE_RULES.preferredAspect) >
      IMAGE_RULES.aspectTolerance
  ) {
    warnings.push('Image is not 4:5 — it will be cropped to fit the pop-up');
  }

  return { errors, warnings };
};

/**
 * Off wins over everything: a disabled pop-up is not "published but hidden" to
 * an admin scanning the tabs, it is off. Published content is retained so the
 * toggle is reversible.
 */
export const derivePopupStatus = (doc: {
  isDisabled: boolean;
  published: PopupContent | null;
}): PopupStatus => {
  if (doc.isDisabled) return 'off';
  return doc.published ? 'published' : 'draft';
};

/**
 * The secondary marker the status badge needs: a single enum cannot express
 * "published, with an unpublished draft on top", which is the state an admin
 * reaches by clicking Save.
 */
export const hasPendingDraft = (doc: {
  draft: PopupContent | null;
  published: PopupContent | null;
}): boolean => !!doc.draft && !!doc.published;
